import { Section } from "views/applications-id-report/components/Section";
import { toMoney } from "shared/utils";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { Show } from "shared/components";
import { CreditCardIcon, Home05Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { ProjectedDebtToIncome } from "./ProjectedDebtToIncome";
import { TotalSummary } from "../types";

export const DebtDetails = ({
  summary,
  isVerificationIncomplete
}: {
  summary: TotalSummary;
  isVerificationIncomplete: boolean;
}) => {
  const hasDebtData = Boolean(
    summary.debt_gross_income_ratio ||
    summary.debt_net_income_ratio ||
    summary.debt_self_attested_gross_income_ratio
  );
  if (!hasDebtData) return null;

  const debts = summary.debts;
  return (
    <Section sectionTitle="Projected debt to income ratio" maxWidth="none">
      <div className="space-y-7 pt-2 pb-8">
        <ProjectedDebtToIncome
          totalMonthlyDebt={summary.total_monthly_debt}
          monthlyIncome={summary.verified_gross_monthly_total}
          monthlyIncomeLabel="Verified gross monthly income"
          debtToIncomeRatio={summary.debt_gross_income_ratio}
          debtToIncomeRatioLabel="Debt to gross income ratio"
          isProcessing={isVerificationIncomplete}
        />
        <ProjectedDebtToIncome
          totalMonthlyDebt={summary.total_monthly_debt}
          monthlyIncome={summary.verified_net_monthly_total}
          monthlyIncomeLabel="Verified net monthly income"
          debtToIncomeRatio={summary.debt_net_income_ratio}
          debtToIncomeRatioLabel="Debt to net income ratio"
          isProcessing={isVerificationIncomplete}
        />
        <ProjectedDebtToIncome
          totalMonthlyDebt={summary.total_monthly_debt}
          monthlyIncome={summary.self_reported_gross_monthly_total}
          monthlyIncomeLabel="Self-reported gross monthly income"
          debtToIncomeRatio={summary.debt_self_attested_gross_income_ratio}
          debtToIncomeRatioLabel="Debt to gross income ratio"
          isProcessing={isVerificationIncomplete}
        />
      </div>
      <Show if={debts.length > 0}>
        <ContentTitle
          title="Monthly debt includes"
          contentClassName="flex gap-4 flex-col pb-6 pt-5"
          titleVariant="medium"
        >
          {debts.map((debt) => {
            return (
              <div
                className="flex justify-between items-center"
                key={debt.name + debt.amount}
              >
                <div className="flex gap-4 items-center">
                  {debt.name === "listed_rent_amount" ? (
                    <HugeiconsIcon
                      icon={Home05Icon}
                      size={20}
                      strokeWidth={1.5}
                      className="text-content-400"
                    />
                  ) : (
                    <HugeiconsIcon
                      icon={CreditCardIcon}
                      size={20}
                      strokeWidth={1.5}
                      className="text-content-400"
                    />
                  )}

                  <div className="space-y-2 text-content-400">
                    <p className="text-content-500 text-sm font-medium first-letter:uppercase">
                      {debt.name === "listed_rent_amount"
                        ? "Application rent"
                        : debt.name.replaceAll("_", " ") || "-"}
                    </p>
                    <p className="text-xs">{debt.description || "-"}</p>
                  </div>
                </div>

                <p className="text-content-1000 font-medium text-sm">
                  {toMoney(debt.amount.cents / 100)}
                </p>
              </div>
            );
          })}
        </ContentTitle>
      </Show>

      <div className="border-b border-alpha-40 md:px-6 pl-4" />
    </Section>
  );
};
